import {WorkOrderLinks} from '../components/work-order-links';
import {InstallerScope} from '../components/installer-scope'; // Installer scope sheet (24 Oct 2026)
import {CashPayments} from '../components/cash-payments';
import {PaymentRequests} from '../components/payment-requests';
import {JobWorkingProposal} from '../components/job-working-proposal';
import {JobProfitabilityPanel} from '../components/job-profitability';
import {JobOverview} from '../components/job-overview';
import {hasQuotedSkirting} from '../lib/job-skirting';
import {JobAddressEditor} from '../components/job-address-editor';
import {PropertyFacts} from '../components/property-facts';
import {InvoiceStatus} from "../components/invoice-status";
import {PaymentRecords} from '../components/payment-records';
import {ReopenJob} from '../components/reopen-job';
import {ClosedJobActions} from '../components/closed-job-actions';
import {ActivityHistory} from "../components/activity-history";
import {ScheduleJob} from "../components/schedule-job";
// Job card: saved records and explicit source limitations. Edits are proposals.
// Full operational feeds remain linked to the platform during the parallel run.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { sydneyDayOf, type Case } from "@artemis-group/aiw-contracts";
import { CaseCard, cn } from "@aiw/ui";
import { Page, Panel } from "../shell/Page";
import { useCases } from "../hooks/useCases";
import { PoDeliveryControl } from "../components/po-delivery-control";
import { BillToControl } from "../components/bill-to-control";
import { useAccount, useJob, useJobInstalls, useJobQuotes, useLead } from "../hooks/useReads";
import { useContact, useJobMaterials, useNow, useProposal, type Install, type InstallDay, type Job as JobRow, type Lead, type Quote } from "../hooks/slice-a";
import { SIDE_STAGES, STAGES, aud, day, m2, sourceLabel, time } from "../lib/format";

import { env } from "../lib/env";
import { mapsLink } from "../lib/api";
import { useT } from "../lib/i18n";
import { recordedInstallDays, jobRecordHistory } from "../lib/job-history";
import { Avatar, Chip, Kv, Pill, ProposalToast, Seg, StagePill, selectCls } from "../components/job-bits";
import { MaterialsTable, PurchaseOrderMaterials, deriveMaterials } from "../components/job-materials";
import { Whiteboard } from "../components/job-whiteboard";
import { costWords } from "../components/lead-drawer";
import {JobSkirting} from '../components/job-skirting';
import {JobCustomerPage} from "../components/job-customer-page";
import {InstallationNotice} from '../components/installation-notice';
import {WorkTabs} from '../components/work-tabs';
import {JobDeliverables} from '../components/job-deliverables';
import {JobCompletionReview} from '../components/job-completion-review';
import {OfficeInstallConfirmation} from '../components/office-install-confirmation';
import {OfficeInstallDay} from '../components/office-install-day';
import {JobDocumentActions} from '../components/job-document-actions';
import {JobStrataPack} from '../components/job-strata-pack';
import {RecordFiles} from '../components/record-files';

const GATES = ["Pending Schedule", "Scheduled", "Install Complete"];
const JOB_TABS = ['overview', 'quote', 'installer-fee', 'installation', 'notes'] as const;
type JobTab = (typeof JOB_TABS)[number];
const PAYMENT = ["TBC", "Transfer", "Cash"];

export function Job() {
  const { t } = useT();
  const { ref = "" } = useParams();
  const location = useLocation();
  const installationPanel = useRef<HTMLDivElement>(null);
  const overviewHeader = useRef<HTMLElement>(null);
  const job = useJob(ref);
  const installs = useJobInstalls(ref);
  const quotes = useJobQuotes(ref);
  const materials = useJobMaterials(ref);
  const propose = useProposal();
  const now = useNow(60_000);
  const { cases, decide, status:caseStatus, error:caseError } = useCases();
  const j = job.data as unknown as JobRow | undefined;
  const lead = useLead(j?.lead_id ?? "");
  const l = lead.data as unknown as Lead | undefined;
  const contactQ = useContact(j?.contact_id ?? "");
  // The billing account is a public.accounts row (a_… id), read through /api/accounts/:id. Asking the contacts route
  // for it answered null, and the card read "Linked account could not be loaded" on Job 10189 (live check, 29 Oct 2026).
  const accountQ = useAccount(j?.account_id ?? "");
  const contact = contactQ.contact, account = (accountQ.data ?? null) as { id: string; name: string; job_count?: number | null } | null;

  const jobReady = !!j && !job.isPending;
  const tab: JobTab = location.hash === '#install' || location.hash === '#installer-scope' ? 'installation' : location.hash === '#skirting' ? 'quote' : (JOB_TABS.find((k) => location.hash === `#${k}`) ?? 'overview');
  useEffect(() => { if (jobReady) overviewHeader.current?.scrollIntoView({block:'start'}); }, [location.key, tab, jobReady]);
  // "Send to installer" opens the Installation tab, where the scope sheet is the first panel. It does not scroll to the
  // panel: the Job header is pinned, and scrolling the panel to the top hid it behind the header (live check, 25 Oct 2026).

  if (job.isPending) return <Page title="Job card" lede={ref}><p className="text-sm text-muted-foreground">Loading {ref}…</p></Page>;
  if (!j) return <Page title="No such job" lede={ref}><p className="text-sm text-muted-foreground">Nothing at this ref. <Link className="text-primary" to="/jobs">Back to jobs</Link></p></Page>;

  if(["New","Quoted","Quoting","Project Bidding","SMG Pending","draft"].includes(j.effective_stage??""))return <Navigate to={`/pipeline/legacy/${ref}`} replace/>;

  const days = recordedInstallDays(installs.data as unknown as Install[] | undefined);
  const installRows = (installs.data ?? []) as unknown as Install[];
  const editableInstall = installRows.length === 1 ? installRows[0] : null;
  const jobCases = cases.filter((c) => c.kase.job_ref === ref);
  const qs = (quotes.data ?? []) as unknown as Quote[];
  const primary = qs.find((q) => q.is_primary) ?? qs[0];
  const mats = env.mock ? (materials.data?.length ? materials.data : deriveMaterials(j, days)) : [];
  const atRisk = mats.filter((m) => m.status === "at risk" || m.status === "not ordered").length;
  const deliveryRecord = ["Pending Schedule","Installation Scheduled","Invoiced","Fully Paid"].includes(j.raw_stage ?? "");
  const schedulingOpen = ["Pending Schedule","Installation Scheduled"].includes(j.raw_stage ?? "");
  const stageIdx = STAGES.indexOf(j.effective_stage as (typeof STAGES)[number]);
  const gateIdx = GATES.indexOf(j.install_gate ?? "");
  const closeDate = j.close_date;
  const crew = editableInstall?.team ?? days[0]?.crew ?? "";
  const owner = l?.owner ?? null;

  /**
   * One field, one proposal. THROWS when it did not happen.
   *
   * This is the fix for "add address, enter it and nothing save": `propose.run` never throws — it
   * reports through the toast — so an inline field called it, saw no exception, and showed itself as
   * saved while the value quietly reverted (or, worse, the write was rejected and nothing said so).
   * The field now gets the outcome and can show it where the person is looking.
   */
  const save = (field: string) => async (value: unknown) => {
    const r = await propose.run("update_job", { job_ref: ref, field, value }, `${fieldWords(field)} changed`);
    if (!r || r.status !== "executed") throw Error(r && "reason" in r ? String(r.reason) : "the change was not saved");
  };
  const moveStage = async (stage: string) => { if (stage && stage !== j.effective_stage) await propose.run("update_stage", { job_ref: ref, stage, reason: "moved from the job card" }, `${j.name ?? ref} → ${stage}`); };

  return (
    <div className="page py-5">
      <div className="mb-3 flex items-center gap-2 text-micro text-muted-foreground"><Link className="text-primary" to="/jobs">Jobs</Link><span>/</span><span className="font-mono">{ref}</span></div>
      <header ref={overviewHeader} className="sticky top-0 z-20 -mx-1 mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-border bg-background px-1 pb-3 pt-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight"><InlineText value={j.name ?? ""} onSave={save("name")} placeholder="name this job" /></h1>
            <StagePill stage={j.effective_stage} />
            {atRisk > 0 && <Pill tone="warn">{atRisk === 1 ? "1 material at risk" : `${atRisk} materials at risk`}</Pill>}
            {j.workflow_flags?.possible_duplicate ? <Pill tone="warn">possible duplicate</Pill> : null}
          </div>
          <p className="mt-1 font-mono text-sm font-semibold text-primary">{t('job.reference')} · {ref}</p>
          <div className="mt-0.5 text-sm text-muted-foreground">
            <JobAddressEditor value={j.address ?? ""} suburb={j.suburb ?? ""} onSave={save("address")} />
            {j.address?.trim() && <a className="ml-1 text-micro text-primary underline" href={mapsLink(`${j.address} ${j.suburb ?? ""}`.trim())} target="_blank" rel="noreferrer">{t('job.openInMaps')}</a>}
            {j.area_m2 ? ` · ${m2(j.area_m2)}` : ""}{j.product ? ` ${j.product}` : ""}{j.stairs ? ` + ${j.stairs} stairs` : ""}{owner ? ` · owner ${owner}` : ""}
            {j.address?.trim() && <div className="mt-0.5"><PropertyFacts address={j.address} suburb={j.suburb ?? null} compact /></div>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {(j.tags ?? []).map((t) => <Chip key={t}>{t}</Chip>)}
            <TagAdder onAdd={async (t) => save("tags")([...(j.tags ?? []), t])} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!env.mock&&<Link className="inline-flex min-h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground" to={`/jobs/${encodeURIComponent(ref)}#installer-scope`}>{t('scope.headerButton')}</Link>}
          <label className="inline-flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">Move stage</span>
            <select className={selectCls} value="" onChange={(e) => void moveStage(e.target.value)} aria-label="Move stage" disabled={propose.busy || !deliveryRecord}>
              <option value="">…</option>
              {[...STAGES.filter(s=>!["New","Quoted"].includes(s)), ...SIDE_STAGES].filter((s) => s !== j.effective_stage).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
      </header>

      <WorkTabs
        active={tab==='overview'?'overview':tab}
        sections={[{key:'quote',label:t('job.tab.quote'),href:`/jobs/${encodeURIComponent(ref)}#quote`},{key:'installer-fee',label:t('job.profit.title'),href:`/jobs/${encodeURIComponent(ref)}#installer-fee`},{key:'installation',label:t('workTabs.installation'),href:`/jobs/${encodeURIComponent(ref)}#installation`},{key:'notes',label:t('job.tab.notes'),href:`/jobs/${encodeURIComponent(ref)}#notes`}]}
        overviewHref={`/jobs/${encodeURIComponent(ref)}`}
        canvasHref={`/work-record/${encodeURIComponent(ref)}`}
        proposalHref={`/proposals/new?${j.lead_id?`lead_id=${j.lead_id}`:`job_ref=${ref}`}`}
        documentsHref={`/archive?job=${encodeURIComponent(ref)}`}
      />

      {!deliveryRecord&&<p className="mb-3 text-sm text-muted-foreground">This reference is an enquiry or a historical record whose acceptance needs review. Stage changes are unavailable here. Use the accepted-offer workflow in Leads for a new job; retain this reference for history.</p>}
      {['Lost','Stalled'].includes(j.raw_stage??'')&&<ClosedJobActions key={ref} jobRef={ref} stage={j.raw_stage??''} reopen={<ReopenJob jobRef={ref} quotes={qs} loading={quotes.isPending||installs.isPending} error={quotes.isError||installs.isError} hasInstall={!!installs.data?.length} hasPaidDate={!!j.date_paid||!!j.close_date} embedded/>}/>}
      <div className="mb-4 rounded-lg border bg-card px-4 py-2.5">
        <ol className="flex gap-1">
          {STAGES.map((s, i) => (
            <li key={s} className="min-w-0 flex-1">
              <div className={cn("h-1.5 rounded-full", i <= stageIdx ? "bg-primary" : "bg-muted")} />
              <div className={cn("mt-1 truncate text-micro", i === stageIdx ? "font-semibold text-foreground" : "text-muted-foreground")} title={s}>{s}</div>
            </li>
          ))}
        </ol>
      </div>


      {/* Jacky, 4 October 2026: "show a high level summary within 1 page view, then move rest to tab with its own view,
          and consolidate stuff like Notes". Overview is the one screen; every other section is a tab of its own. */}
      {tab==='overview'&&<div className="space-y-4">
          {caseStatus === "error"&&<p role="alert" className="text-sm text-destructive">Approval updates are unavailable. Refresh before relying on this job’s case status.</p>}
          {caseError&&<p role="alert" className="text-sm text-destructive">{caseError}</p>}
          {jobCases.map((c) => <div key={c.kase.id} className="space-y-2"><CaseCard kase={c.kase} state={c.state} busy={c.busy} size="full" onDecide={(d, x) => decide(c.kase.id, d, x)} /><PoDeliveryControl kase={c.kase}/></div>)}

        <JobOverview job={j} jobRef={ref} days={days} gateIdx={gateIdx} quotes={qs} crewLabel={crew}/>
        <div className="grid gap-4 md:grid-cols-2">
          <Panel title="Contact" meta="read-only">
            {contact ? (
              <>
                <div className="flex items-center gap-3">
                  <Avatar name={contact.name} size="lg" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{contact.name}</div>
                    <div className="truncate text-micro text-muted-foreground">{[contact.phone, contact.email, contact.prefers ? `prefers ${contact.prefers}` : null].filter(Boolean).join(" · ")}</div>
                  </div>
                  <Link className="text-micro text-primary" to={`/contacts/${contact.id}`}>360</Link>
                </div>
                <div className="my-3 h-px bg-border" />
              </>
            ) : <p className="mb-3 text-sm text-muted-foreground">{j.contact_id ? "Contact not in this list yet." : "No contact on the job."}</p>}
            <Kv rows={[
              ["Lead", l ? <><Chip>{sourceLabel(l.source_id || "none")}{l.facet ? ` · ${l.facet}` : ""}</Chip> {day(l.received_at)} · <span className="num text-gold">{costWords(l)}</span></> : <span className="text-muted-foreground">no lead on file</span>],
              ["Lifecycle", contact ? <Pill tone={contact.lifecycle === "customer" ? "ok" : "info"}>{contact.lifecycle ?? "—"}{j.outcome ? ` · ${j.outcome}` : ""}</Pill> : "—"],
            ]} />
          </Panel>
          <JobCustomerPage jobRef={ref} contactId={j.contact_id??null} contactName={contact?.name??null} propose={propose}/>
          <Panel title="Account & billing" meta="who the invoice names">
            <Kv rows={[
              ["Account", account ? <Link className="text-primary" to={`/accounts/${account.id}`}>{account.name}</Link> : j.account_id ? "Linked account could not be loaded" : "No account linked"],
              ["Address", j.address ?? "—"],
              ["Homes", "Open the customer record for saved homes and measurements"],
              ["Jobs", account?.job_count == null ? "not available here" : String(account.job_count)],
            ]} />
            {/* The one place the billing decision is made. The party is resolved
                server-side so this card and any document builder read the same
                answer — see services/api/src/billing-party.ts. */}
            <div className="mt-3 border-t pt-3">
              <BillToControl jobRef={ref} />
            </div>
          </Panel>
        </div>
      </div>}
      {tab==='quote'&&<div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Panel id="job" title="Job" meta="click a value to edit">
              <Kv rows={[
                ["Name", <InlineText value={j.name ?? ""} onSave={save("name")} placeholder="name this job" />],
                ["Payment method", <><InlineSelect value={j.payment_method ?? "TBC"} options={PAYMENT} onSave={save("payment_method")} /> <span className="text-micro text-muted-foreground">TBC / Transfer / Cash</span></>],
                [t('cash.title'), <CashPayments jobRef={ref}/>],
                [t('payreq.title'), <PaymentRequests jobRef={ref}/>],
                [t('job.workingProposal'), <JobWorkingProposal jobRef={ref} compact/>],
                [t('job.recordedValue'), <InlineText value={j.deal_value ?? ""} type="number" money onSave={(v) => save("deal_value")(v === "" ? null : Number(v))} placeholder="not quoted yet" />],
                [t('job.depositReceived'), j.cash_payments_active?<div>{aud(j.deposit_amount??0)}<p className="text-sm text-muted-foreground">{t('cash.managedDeposit')}</p></div>:<div className="space-y-1"><div><span>{t('job.depositAmount')} </span><InlineText value={j.deposit_amount ?? ""} type="number" money onSave={v=>save("deposit_amount")(v===""?null:Number(v))} placeholder={t('job.depositUnrecorded')}/></div><div><span>{t('job.depositPercent')} </span><InlineText value={j.deposit_amount!=null&&Number(j.deal_value)>0?Math.round(j.deposit_amount/Number(j.deal_value)*10000)/100:j.deposit_pct??""} type="number" suffix=" %" onSave={v=>v===""?save("deposit_amount")(null):save("deposit_pct")(Number(v))} placeholder="%"/></div><InlineText value={isoDate(j.deposit_date)} type="date" onSave={v=>save("deposit_date")(v||null)} placeholder={t('job.depositDate')}/><p className="text-micro text-muted-foreground">{t('job.depositHelp')}</p></div>],
                ["Date paid", <InlineText value={isoDate(j.date_paid)} type="date" onSave={(v) => save("date_paid")(v || null)} placeholder="not paid" />],
                ["Address", <JobAddressEditor value={j.address ?? ""} suburb={j.suburb ?? ""} onSave={save("address")} />],
                ["Stage", <InlineSelect value={j.effective_stage ?? "New"} options={[...STAGES, ...SIDE_STAGES]} onSave={(v) => moveStage(String(v))} />],
                ["Close date", <><span className="text-muted-foreground">{closeDate ? day(closeDate) : "not recorded"}</span></>],
              ]} />
            </Panel>
            <Panel id="quote" title={t('job.quoteProposal')} meta={<span className="rounded-full bg-gold/10 px-2 text-gold">staff only</span>}>
              <JobWorkingProposal jobRef={ref}/><h3 className="text-sm font-semibold">{t('job.savedQuotes')}</h3>
              {qs.length === 0 && <p className="text-sm text-muted-foreground">No quote yet.</p>}
              <ul className="divide-y">
                {qs.map((q) => (
                  <li key={q.id} className="flex items-center gap-2 py-2 text-sm">
                    <span className="min-w-0 flex-1"><span className="font-medium">{q.option_name ?? "Option"}</span>{q.is_primary && <Pill tone="info" className="ml-1.5">primary</Pill>}<span className="block text-micro text-muted-foreground">{day(q.created_at)}{q.subtotal != null ? ` · subtotal ${aud(q.subtotal)}` : ""}</span></span>
                    <span className="num text-gold">{q.subtotal == null ? '—' : `${aud(q.subtotal)} + GST`}</span>
                    <Link className="text-micro text-primary underline-offset-2 hover:underline" to={`/quotes/${q.id}`}>{t('quote.open')}</Link>
                  </li>
                ))}
              </ul>
              {j.deposit_date && (
                <p className="mt-2 text-micro text-muted-foreground">{t('job.depositRecorded', {date:day(j.deposit_date),amount:j.deposit_amount==null?t('job.depositUnrecorded'):aud(j.deposit_amount)})}</p>
              )}
            </Panel>
          </div>
          {/* Skirting and door architrave belong with the quote (Jacky, 4 October 2026). */}
          <div id="skirting" className="scroll-mt-24"><JobSkirting jobRef={ref} propose={propose} acceptedQuote={primary?{id:primary.id,option_name:primary.option_name,hasSkirting:hasQuotedSkirting(primary)}:null}/></div>

        </div>
        <div className="space-y-4">
          <InvoiceStatus jobRef={ref} />
          <PaymentRecords jobRef={ref} />
        </div>
      </div>}
      {tab==='installer-fee'&&<div className="space-y-4">
            <Panel id="installer-fee" title={t('job.profit.title')} meta={<span className="rounded-full bg-gold/10 px-2 text-gold">{t('priced.profit.staffOnly')}</span>}>
              <JobProfitabilityPanel jobRef={ref}/>
            </Panel>
      </div>}
      {tab==='installation'&&<div className="space-y-4">
          {/* At the top, not below the scheduler: Jacky, 25 October 2026, could not find it there ("why am I keep losing this build"). */}
          {!env.mock&&<div className="scroll-mt-24 rounded-lg border-2 border-primary/40"><InstallerScope jobRef={ref} suburb={j.suburb ?? ''}/></div>}
          <Panel id="installation-notice" collapsible defaultOpen={['Pending Schedule','Installation Scheduled'].includes(j.effective_stage??'')} title={t('installationNotice.title')}>
            <InstallationNotice key={ref} jobRef={ref} active={['Pending Schedule','Installation Scheduled'].includes(j.effective_stage??'')} propose={propose}/>
          </Panel>
          <div ref={installationPanel} id="installation" className="scroll-mt-24"><Panel
            title="Installation"
            meta={<ol className="flex items-center gap-1.5">{GATES.map((g, i) => <li key={g} className={cn("rounded px-1.5 leading-5", i < gateIdx ? "bg-success/15 text-success" : i === gateIdx ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>{g}</li>)}</ol>}
          >
            <div className="mb-3 flex justify-end gap-2"><Link className="inline-flex min-h-10 items-center rounded-md border px-3 text-sm font-medium text-primary" to={`/work-record/${encodeURIComponent(ref)}`}>{t('canvas.openSiteCanvas')}</Link><Link className="inline-flex min-h-10 items-center rounded-md border px-3 text-sm font-medium text-primary" to={`/carpet-run/${encodeURIComponent(ref)}`}>{t('carpetRun.open')}</Link></div>
            {/* BOOKING A CREW-DAY. Jacky: "where do I schedule work on a job that is accepted, I
                havent seen that function." It was nowhere — the CRM read the schedule and never
                booked one. This panel is the screen: it goes through POST /propose like every other
                change, so the gate, the next date and the job stage stay DERIVED. */}
            {installRows.length <= 1 ? <ScheduleJob
              jobRef={ref}
              installId={editableInstall?.id ?? null}
              updatedAt={editableInstall?.updated_at ?? null}
              crew={crew}
              days={(editableInstall?.days ?? []).map((d) => ({id:d.id,date:d.date,description:d.task ?? "",done:d.done,crew:d.crew,assignee:d.assignee,taskIds:d.task_ids??[]}))}
              active={schedulingOpen}
              onScheduled={() => void installs.refetch()}
              portalUrl={env.installerPortal}
            /> : <p className="mt-3 text-sm text-warning">{t('schedule.multipleInstalls')}</p>}
            {days.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No crew-day booked yet — the panel above books one. A booked day is a plan; the work order records what happened.</p>}
            <ul className="divide-y">
              {days.map((d) => <OfficeInstallDay key={d.id??d.date+(d.task??"")} jobRef={ref} installationId={editableInstall?.id??null} updatedAt={editableInstall?.updated_at??null} item={d} onSaved={()=>installs.refetch()}/>)}
            </ul>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-micro text-muted-foreground">
              {installs.data?.[0] && <Link className="text-primary underline-offset-2 hover:underline" to={`/jobs/${ref}#install`}>{t('job.installBelow')}</Link>}
              <Link className="text-primary underline-offset-2 hover:underline" to="/calendar">Calendar</Link>
              <span className="ml-auto">A planned date does not confirm attendance. Open the work order for further details.</span>
            </div>
            {!env.mock&&<div className="mt-3"><WorkOrderLinks jobRef={ref}/></div>}
            <JobCompletionReview jobRef={ref}/>
            <OfficeInstallConfirmation jobRef={ref} installationId={editableInstall?.id??null}/>
            <JobDocumentActions job={{ref,contact_id:j.contact_id,address:j.address??null,product:j.product??null,installation_id:editableInstall?.id??null,crew_days:days,install_complete:(j.install_gate??'')==='Install Complete'}}/>
            <JobStrataPack jobRef={ref}/>
            <JobDeliverables jobRef={ref}/>
            <RecordFiles jobRef={ref}/>
          </Panel></div>

          <Panel id="materials" title="Materials due vs schedule" meta={env.mock ? <>{atRisk > 0 && <Pill tone="warn" className="mr-2">{atRisk} at risk</Pill>}Sample schedule</> : t('po.office.linkedOrders')} className={atRisk > 0 ? "border-l-4 border-l-warning" : ""}>
            {env.mock ? <MaterialsTable rows={mats} /> : <PurchaseOrderMaterials jobRef={j.ref}/>}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-micro text-muted-foreground">
              <span>Material status must be verified against the purchase order.</span>
              <Link className="ml-auto text-primary underline-offset-2 hover:underline" to={`/purchase-orders?job_ref=${encodeURIComponent(ref)}`}>{t('po.office.office')}</Link>
              <Link className="text-primary underline-offset-2 hover:underline" to="/purchase-orders">{t('rail.purchaseOrders')}</Link>
            </div>
          </Panel>

      </div>}
      {tab==='notes'&&<div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <ActivityHistory jobRef={ref} open />
        </div>
        <div className="space-y-4">
          <Timeline job={j} lead={l} quotes={qs} days={days} cases={jobCases.map((c) => c.kase)} />
          <div className="grid gap-4 md:grid-cols-2">
            {env.mock&&<Panel title="SMS to installer"><InstallerSms job={j} days={days}/></Panel>}
            <Panel id="site" title="Site assessment">
              <Kv rows={[
                ["Measured", <span className="text-muted-foreground">Assessor and measurement evidence not connected here</span>],
                ["Area", j.area_m2 ? m2(j.area_m2) : <span className="text-muted-foreground">to measure</span>],
                ["Stairs", j.stairs == null ? "not recorded" : j.stairs ? `${j.stairs} · nosing per step` : "none"],
                ["Product", j.product ?? <span className="text-muted-foreground">to confirm</span>],
              ]} />
            </Panel>
          </div>
        </div>
      </div>}
      <ProposalToast note={propose.note} onClose={propose.clear} />
    </div>
  );
}

// ── click-to-edit ──────────────────────────────────────────────────────────
type SaveState = "idle" | "saving" | "error";

export function InlineText({ value, onSave, type = "text", money, suffix = "", placeholder }: { value: string | number; onSave: (v: string) => Promise<void> | void; type?: "text" | "number" | "date"; money?: boolean; suffix?: string; placeholder?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [state, setState] = useState<SaveState>("idle");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const [error, setError] = useState<string | null>(null);
  async function commit() {
    setEditing(false);
    if (draft === String(value)) return;
    setState("saving"); setError(null);
    try { await onSave(draft); setState("idle"); }
    catch (e) {
      // Say what happened, in the place he is looking — not only in a toast at the foot of the page.
      setState("error");
      setError(e instanceof Error ? e.message : "not saved");
      setDraft(String(value)); // the old value is what the record actually holds
    }
  }
  if (editing) {
    return (
      <input
        ref={ref}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void commit(); } if (e.key === "Escape") { setDraft(String(value)); setEditing(false); } }}
        className={cn("inline-block h-7 min-w-[8rem] max-w-full rounded border bg-background px-1.5 font-sans text-sm font-normal focus:outline-none focus:ring-1 focus:ring-ring", money && "num")}
        aria-label="Edit value"
      />
    );
  }
  const shown = value === "" || value == null ? null : type === "date" ? day(String(value)) : money ? aud(Number(value)) : `${value}${suffix}`;
  return (
    <button type="button" onClick={() => setEditing(true)} className={cn("inline max-w-full rounded px-0.5 text-left underline decoration-dotted decoration-border underline-offset-4 hover:bg-accent", money && "num text-gold", shown == null && "text-muted-foreground")} title="Click to edit">
      {shown ?? placeholder ?? "—"}
      {state === "saving" && <span className="ml-1 text-micro text-muted-foreground">saving…</span>}
      {state === "error" && <span role="alert" className="ml-1 text-micro text-warning">not saved — {error}</span>}
      {state === "error" && <span className="ml-1 text-micro text-destructive">not saved</span>}
    </button>
  );
}

export function InlineSelect({ value, options, onSave }: { value: string; options: readonly string[]; onSave: (v: string) => Promise<void> | void }) {
  const [editing, setEditing] = useState(false);
  const [state, setState] = useState<SaveState>("idle");
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);
  async function pick(v: string) {
    setEditing(false);
    if (v === value) return;
    setState("saving");
    try { await onSave(v); setState("idle"); } catch { setState("error"); }
  }
  if (editing) {
    return (
      <select ref={ref} className={cn(selectCls, "h-7")} defaultValue={value} onChange={(e) => void pick(e.target.value)} onBlur={() => setEditing(false)} onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }} aria-label="Choose">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} className="rounded px-0.5 underline decoration-dotted decoration-border underline-offset-4 hover:bg-accent" title="Click to change">
      {value}
      {state === "saving" && <span className="ml-1 text-micro text-muted-foreground">saving…</span>}
      {state === "error" && <span className="ml-1 text-micro text-destructive">not saved</span>}
    </button>
  );
}

function TagAdder({ onAdd }: { onAdd: (t: string) => Promise<void> | void }) {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState("");
  if (!open) return <button type="button" className="text-micro text-primary" onClick={() => setOpen(true)}>+ tag</button>;
  return (
    <input
      autoFocus
      className="h-6 w-28 rounded border bg-background px-1.5 text-micro focus:outline-none focus:ring-1 focus:ring-ring"
      value={t}
      placeholder="tag"
      onChange={(e) => setT(e.target.value)}
      onBlur={() => { setOpen(false); setT(""); }}
      onKeyDown={(e) => { if (e.key === "Enter" && t.trim()) { void onAdd(t.trim()); setOpen(false); setT(""); } if (e.key === "Escape") { setOpen(false); setT(""); } }}
      aria-label="New tag"
    />
  );
}

// ── the installer's SMS, in the crew's language ────────────────────────────
const ZH_DOW = ["日", "一", "二", "三", "四", "五", "六"];
// The Sydney calendar day, read from its YYYY-MM-DD — never the device's month/day.
const zhDate = (iso: string) => { const d = new Date(`${sydneyDayOf(iso) ?? iso.slice(0, 10)}T12:00:00Z`); return `${d.getUTCMonth() + 1}月${d.getUTCDate()}日（周${ZH_DOW[d.getUTCDay()]}）`; };
const enRange = (a: string, b?: string) => (b && b !== a ? `${day(a)} – ${day(b)}` : day(a));

function InstallerSms({ job, days }: { job: JobRow; days: InstallDay[] }) {
  const sorted = days.slice().sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0]?.date, last = sorted[sorted.length - 1]?.date;
  const n = sorted.length;
  const product = (job.product ?? "").replace(/^[^']*'([^']+)'.*$/, "$1") || job.product || "地板";
  const area = job.area_m2 ? `${job.area_m2} m²` : "";
  const stairsZh = job.stairs ? ` 加 ${job.stairs} 级楼梯` : "";
  const stairsEn = job.stairs ? ` + ${job.stairs} stairs` : "";
  const when = first ? (last && last !== first ? `${zhDate(first)}至${zhDate(last)}` : zhDate(first)) : "日期待定";
  const daysZh = n ? `，${["", "一", "两", "三", "四", "五", "六", "七"][n] ?? n}天` : "";
  const zh = `新工单 ${job.ref} — ${job.suburb ?? ""}，${(job.address ?? "").replace(/,.*$/, "")}。${when}，${product} ${area}${stairsZh}${daysZh}。铺第一排前请先做放线核对；钥匙箱密码当天见工单。`;
  const en = `New work order ${job.ref} — ${job.suburb ?? ""}, ${(job.address ?? "").replace(/,.*$/, "")}. ${first ? enRange(first, last) : "date to confirm"}, ${product} ${area}${stairsEn}${n ? `, ${n === 1 ? "one day" : `${n} days`}` : ""}. Set-out check before the first row; key-safe code on the work order on the day.`;
  return (
    <div className="space-y-2">
      <div className="rounded-md border bg-background p-3 text-sm leading-relaxed">
        {zh}
        <div className="mt-2 border-t pt-2 text-micro text-muted-foreground">Sends when a day is booked or moved · the crew reads it in their language · the same words go to a 中文 crew on every job</div>
      </div>
      <p className="text-micro text-muted-foreground">Only the message is translated; the CRM stays English. For the office: “{en}”</p>
    </div>
  );
}

// ── timeline ───────────────────────────────────────────────────────────────
type Ev = { at: string; kind: "key" | "money" | "emma" | "note" | "plain"; title: string; detail?: ReactNode };
type Filter = "all" | "key";

function Timeline({ job, lead, quotes, days, cases }: { job: JobRow; lead: Lead | undefined; quotes: Quote[]; days: InstallDay[]; cases: Case[] }) {
  const [f, setF] = useState<Filter>("all");
  const evs: Ev[] = jobRecordHistory(job,quotes,days);
  if (lead) evs.push({ at: lead.received_at, kind: "plain", title: "Lead received", detail: sourceLabel(lead.source_id || "none") });
  for (const k of cases) evs.push({ at: k.created_at, kind: "emma", title: "EmmA case created", detail: k.questions.why_now.trigger });
  const shown = evs.filter((e) => f === "all" ? true : f === "key" ? e.kind === "key" || e.kind === "money" || e.kind === "emma" : false).sort((a, b) => b.at.localeCompare(a.at));

  return (
    <Panel id="timeline" collapsible defaultOpen={false} summary={`${shown.length} dates`} title="Timeline" meta={<Seg value={f} onChange={setF} items={[{ id: "all", label: "All" }, { id: "key", label: "Key" }]} />}>
      <p className="mb-3 text-sm text-muted-foreground">Saved record dates. See Operational history for recorded events and notes. Scheduled days come from the saved installation record. Use Payment records from Xero for the on-demand payment check.</p>
      {shown.length === 0 && <p className="text-sm text-muted-foreground">No record dates available here.</p>}
      <ul className="space-y-2">
        {shown.map((e, i) => {
          const d = new Date(e.at);
          const today = d.toDateString() === new Date().toDateString();
          return (
            <li key={`${e.at}-${i}`} className="grid grid-cols-[4.5rem_auto_minmax(0,1fr)] items-start gap-2 text-sm">
              <span className="num pt-0.5 text-micro text-muted-foreground">{today ? time(e.at) : day(e.at)}</span>
              <span className={cn("mt-1.5 h-2 w-2 rounded-full", e.kind === "key" ? "bg-primary" : e.kind === "money" ? "bg-gold" : e.kind === "emma" ? "bg-rail" : e.kind === "note" ? "bg-warning" : "bg-border")} aria-hidden />
              <span className="min-w-0"><span className="font-medium">{e.title}</span>{e.detail ? <span className="text-muted-foreground"> · {e.detail}</span> : null}</span>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

// SYDNEY, not UTC: an instant at 08:30 Sydney is 22:30 UTC the day before, so this used to put
// yesterday's date in the field — and saving it recorded the wrong day (Jacky, 18 Sep).
const isoDate = (iso: string | null | undefined) => sydneyDayOf(iso) ?? "";
const fieldWords = (f: string) => ({ name: "Name", address: "Address", payment_method: "Payment method", deal_value: "Deal value", deposit_amount: "Deposit amount", deposit_pct: "Deposit %", deposit_date: "Deposit date", date_paid: "Date paid", tags: "Tags" } as Record<string, string>)[f] ?? f.replace(/_/g, " ");
